/**
 * Garden drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const gardenDrawingsPack: ToilePack = {
    slug: 'garden-drawings',
    category: 'illustration',
    name: 'Garden drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'arch', label: 'Rose arch', subject: 'a garden path under a rose arch, lavender along the edges and a bench waiting at the end'},
        {key: 'pond', label: 'Lily pond', subject: 'a lily pond with a small wooden bridge, irises at the bank and a dragonfly over the water'},
        {key: 'greenhouse', label: 'Greenhouse', subject: 'a greenhouse corner with clay pots, seedlings, a watering can and a trowel on the bench'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['tulips', 'a fountain', 'winter'],
    directionHint: 'try: tulips',
    seo: {
        title: 'Garden Drawings: Rose Arch, Lily Pond, Greenhouse',
        metaDescription: 'Nine garden drawings: a rose arch over a path, a lily pond with a small bridge and a greenhouse corner, each in pencil, pen and ink, or charcoal.',
        h1: 'Garden drawings: a rose arch, a lily pond, a greenhouse corner',
        tagline: 'Three garden scenes, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three garden scenes: a path under a rose arch with lavender along the edges, a lily pond with a wooden bridge and irises at the bank, and a greenhouse corner of clay pots and seedlings. Each is drawn in pencil, pen and ink, and charcoal, so the dense leaf and flower detail can be studied in three media. Type a different garden or a twist, such as tulips or a fountain, and the same media carry it.',
        useCases: [
            'a detailed study of leaf and flower',
            'a framed print for a porch or hallway',
            'a copy-along drawing exercise',
            'a botanical practice sheet',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three garden scenes: a rose arch over a path, a lily pond with a bridge, and a greenhouse corner. Each scene appears in three media.'},
            {q: 'Can I ask for a different garden?', a: 'Yes. Type a subject or twist, such as tulips, a fountain or winter. A typed direction is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Pencil shades from sharp detail to deep darks on plain white paper. Pen and ink uses contour lines and stippling. Charcoal uses smudged tone with lifted highlights.'},
            {q: 'Is each drawing wide or tall?', a: 'Tall. The garden drawings are taller than they are wide.'},
        ],
    },
}
